<template>
  <div
    v-show="isShow"
    class="mb-3"
  >
    <img
      ref="image"
      style="max-height: 400px;"
    >

    <div
      v-if="isLoading"
      class="d-flex align-items-center justify-content-center"
    >
      <b-spinner variant="primary" />
      <span class="ml-2">
        Loading...
      </span>
    </div>
    <div v-else>
      <b-form-input
        v-model="filename"
        class="my-3"
      />

      <b-input-group class="align-items-center mb-3">
        <b-form-input
          v-model.number="width"
          type="number"
        />
        <b-icon
          icon="x-lg"
          class="mx-2"
        />
        <b-form-input
          v-model.number="height"
          type="number"
        />
        <div class="image-editor-actions image-editor-actions--bg-actions">
          <button
            v-b-tooltip.hover
            class="crop"
            title="Crop to image"
            @click="onCropToImage"
          >
            <img
              src="/img/icons/cropToImage.svg"
              style="width: 25px; height: 25px;"
            >
          </button>
        </div>
      </b-input-group>

      <b-button
        variant="primary"
        :disabled="isSaving || !filename"
        @click="onSave"
      >
        {{ isSaving ? 'Saving...' : 'Save' }}
      </b-button>

      <b-button
        variant="primary"
        @click="cropperInstance.reset()"
      >
        Reset
      </b-button>

      <b-button
        variant="primary"
        @click="$emit('onCancel')"
      >
        Cancel
      </b-button>
    </div>
  </div>
</template>

<script>
import Cropper from "cropperjs";
import Swal from 'sweetalert2';
import FileUploadService from '@frontend/services/api/file-upload';

export default {
  props: {
    src: {
      type: String,
      default: '',
    },
    isShow: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      cropperInstance: null,
      filename: '',
      width: 0,
      height: 0
    };
  },
  watch: {
    isShow() {
      if (this.isShow) {
        this.init();
      } else {
        this.destroy();
      }
    },
    src() {
      if (this.isShow) {
        this.destroy();
        this.init();
      }
    }
  },
  methods: {
    getSourceFilename() {
      let source = this.src || '';

      try {
        const url = new URL(source, window.location.origin);
        source = url.searchParams.get('file') || url.pathname;
      } catch (error) {
        const fileParam = source.match(/[?&]file=([^&]+)/);
        source = fileParam ? fileParam[1] : source;
      }

      const clean = decodeURIComponent(source)
        .split("?")[0]
        .split("&")[0]
        .split("/")
        .pop() || 'image.png';

      const lastDotIndex = clean.lastIndexOf(".");
      const base = lastDotIndex > 0 ? clean.slice(0, lastDotIndex) : clean;
      let extension = lastDotIndex > 0 ? clean.slice(lastDotIndex + 1).toLowerCase() : 'png';

      if (!extension || extension === 'undefined' || extension === 'null') {
        extension = 'png';
      }

      return {
        base: base.split("_cropped_")[0] || 'image',
        extension,
      };
    },
    init() {
      const { base, extension } = this.getSourceFilename();

      const d = new Date();
      const formatted =
        d.getFullYear().toString() +
        String(d.getMonth() + 1).padStart(2, "0") +
        String(d.getDate()).padStart(2, "0") +
        "_" +
        String(d.getHours()).padStart(2, "0") +
        String(d.getMinutes()).padStart(2, "0") +
        String(d.getSeconds()).padStart(2, "0");

      this.filename = `${base}_cropped_${formatted}.${extension}`;

      this.isLoading = true;

      this.$refs.image.onload = async () => {
        await this.initCrop(this.$refs.image);

        this.isLoading = false;
      };

      this.$refs.image.src = this.src;
    },
    initCrop(imageElement) {
      return new Promise((resolve, reject) => {
        this.cropperInstance = new Cropper(imageElement, {
          autoCropArea: 1,
          zoomable: false,
          ready: () => {
            resolve();
          },
          crop: (event) => {
              this.width = Math.round(event.detail.width);
              this.height = Math.round(event.detail.height);
          }
        });
      })
    },
    destroy() {
      this.destroyCropper();
      this.cropperInstance = null;
      this.filename = '';
      this.width = 0;
      this.height = 0;
    },
    destroyCropper() {
      if (this.cropperInstance) {
        this.cropperInstance.destroy()
      }
    },
    onSave() {
      this.isSaving = true;

      const cleanFilename = this.filename.split('?')[0].split('&')[0];
      const extension = cleanFilename.split('.').pop().toLowerCase();

      let mimeType = 'image/png';

      switch (extension) {
        case 'png':
          mimeType = 'image/png';
          break;
        case 'webp':
          mimeType = 'image/webp';
          break;
        case 'jpg':
        case 'jpeg':
          mimeType = 'image/jpeg';
          break;
        default:
          break;
      }

      this.cropperInstance.getCroppedCanvas({
        imageSmoothingEnabled: false,
        imageSmoothingQuality: 'high'
      }).toBlob((blob) => {
        this.$emit('onSave', { blob, fileName: this.filename });
        this.$emit('onCancel');
        this.isSaving = false;
      }, mimeType, 1.0);
    },
    async onCropToImage() {
      try {
        const isFileImage = this.$parent.isFileImage;
        
        if (isFileImage) {
          const result = await Swal.fire({
            title: 'Crop Image Options',
            text: 'How would you like to handle the image cropping?',
            icon: 'question',
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: 'Replace Existing',
            denyButtonText: 'Copy and Replace',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#209c86',
            denyButtonColor: '#209c86',
          });

          if (result.isConfirmed || result.isDenied) {
            const isCopyAndReplace = result.isDenied;
            
            if (result.isConfirmed) {
              const confirmReplace = await Swal.fire({
                title: "Are you sure?",
                text: "This will take effect immediately. You won't be able to revert this!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, replace it!",
              });
              
              if (!confirmReplace.isConfirmed) {
                return;
              }
            }
            
            await this.processCropToImage(isCopyAndReplace);
          }
        } else {
          await this.processCropToImage(false);
        }
      } catch (error) {
        toastr.error('Something went wrong with the crop image dialog');
      }
    },
    async processCropToImage(isCopyAndReplace) {
      try {
        toastr.info(
          `<div class="d-inline-flex align-content-center">Auto cropping image...<div class="spinner-border spinner-border-sm" role="status"><span class="visually-hidden"></span></div></div>`,
          null,
          {
            timeOut: 0,
            tapToDismiss: false,
            closeButton: false
          }
        );

        const filePath = new URL(this.src).searchParams.get('file');
        const isFileImage = this.$parent.isFileImage;

        const { data } = await FileUploadService.cropImage({
          filePath: filePath,
          isFileImage: isFileImage
        });

        this.$emit('onAutoCrop', {
          imgUrl: data.imgUrl,
          filePath,
          fileName: data.fileName,
          isCopyAndReplace: isCopyAndReplace,
          isCropToImage: true
        });

        this.$emit('onCancel');
        toastr.clear();
      } catch (error) {
        toastr.clear();
        toastr.error('Something went wrong while cropping the image');
      }
    }
  }
}
</script>
